/** URL parsing utilities for dataspace routing */

/**
 * Extract dataspace ID from URL string
 *
 * @example
 *   extractDataspaceIdFromUrl('/ds/123/data') => '123'
 *
 * @param url - The URL to parse
 * @returns The dataspace ID as a string, or null if not found
 */
export function extractDataspaceIdFromUrl(url: string): string | null {
  const urlSegments = url.split('/');
  const dsIndex = urlSegments.findIndex((segment) => segment === 'ds');

  if (dsIndex !== -1 && urlSegments.length > dsIndex + 1) {
    const id = urlSegments[dsIndex + 1].split('?')[0]; // Remove query params
    return id || null; // Return null if id is empty string
  }

  return null;
}

/**
 * Extract plan ID from URL string.
 *
 * The `plan` segment is matched exactly, so the `planning-spaces` segment that always
 * precedes it in a plan URL is not mistaken for it.
 *
 * @example
 *   extractPlanIdFromUrl('/ds/1/planning-spaces/ps-1/plan/p-1/data/site') => 'p-1'
 *
 * @param url - The URL to parse
 * @returns The plan ID as a string, or null if the URL is not inside a plan
 */
export function extractPlanIdFromUrl(url: string): string | null {
  const urlSegments = url.split('/');
  const planIndex = urlSegments.findIndex((segment) => segment === 'plan');

  if (planIndex !== -1 && urlSegments.length > planIndex + 1) {
    const id = urlSegments[planIndex + 1].split('?')[0]; // Remove query params
    return id || null; // Return null if id is empty string
  }

  return null;
}

/**
 * Check if URL contains dataspace pattern
 *
 * @example
 *   isDataspaceUrl('/ds/123/data') => true
 *
 * @param url - The URL to check
 * @returns True if the URL contains a valid dataspace pattern
 */
export function isDataspaceUrl(url: string): boolean {
  const urlSegments = url.split('/').filter((segment) => segment !== '');
  return (
    urlSegments.length > 1 && urlSegments[0] === 'ds' && !isNaN(Number(urlSegments[1]))
  );
}

/**
 * Get relative path after dataspace ID
 *
 * @example
 *   getRelativePathFromUrl('/ds/123/data/entities') => 'data/entities'
 *
 * @param url - The URL to parse
 * @returns The relative path after the dataspace ID
 */
export function getRelativePathFromUrl(url: string): string {
  const urlSegments = url.split('/');
  const dsIndex = urlSegments.findIndex((segment) => segment === 'ds');

  if (dsIndex !== -1 && urlSegments.length > dsIndex + 2) {
    return urlSegments
      .slice(dsIndex + 2)
      .join('/')
      .split('?')[0] // Remove query params
      .split('#')[0]; // Remove fragments
  }

  return '';
}

/**
 * Build full dataspace URL
 *
 * @example
 *   buildDataspaceUrl(123, 'data/entities') => '/ds/123/data/entities'
 *
 * @param dataspaceId - The dataspace ID (number or string)
 * @param relativePath - Optional relative path to append
 * @returns The constructed dataspace URL
 */
export function buildDataspaceUrl(
  dataspaceId: number | string,
  relativePath?: string
): string {
  const path = ['ds', dataspaceId.toString()];

  if (relativePath) {
    const sanitizedPath = relativePath.startsWith('/')
      ? relativePath.slice(1)
      : relativePath;
    path.push(...sanitizedPath.split('/').filter((segment) => segment));
  }

  return '/' + path.join('/');
}
